import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import clsx from 'clsx';

import FilterActionButton from '../../../../components/designe/ActionButtons/FilterActionButton';
import AppAddPillButton from '../../../../components/designe/buttons/app-add-pill-button/AppAddPillButton';
import AppDialog from '../../../../components/reusable/app-dialog/AppDialog';
import ConfirmDialog from '../../../../components/reusable/confirm-dialog/ConfirmDialog';
import NoResults from '../../../../components/reusable/no-results';
import Search from '../../../../components/reusable/search/Search';
import WithListObservers from '../../../../hoc/with-list-observers';
import { IExercise } from '../../../../types/interfaces/exercise';
import {
  ExerciseFiltersModal,
  IExerciseFilterOption,
  IExerciseFilters,
  cloneExerciseFilters,
  filterExercises,
  sanitizeExerciseFilters,
  useExerciseFilters,
} from '../exercise-filters';
import ExerciseItemSkeleton from '../exercise-item-skeleton/ExerciseItemSkeleton';
import ExerciseItem from '../exercise-item/ExerciseItem';
import PublishExerciseModal from '../pubish-exercise-modal/PublishExerciseModal';
import { useStyles } from './ExcerciseList.styles';
import type { IExerciseListFilterQuery } from './exercise-list-filter-query';
import { useExerciseList } from './useExcerciseList';

const SKELETON_COUNT = 4;

function buildFilterOptions(
  exercises: IExercise[] | null,
  getValues: (
    exercise: IExercise,
  ) => Array<
    { id?: string; name?: string; nameKey?: string } | null | undefined
  >,
): IExerciseFilterOption[] {
  if (!exercises?.length) {
    return [];
  }

  const optionsById = new Map<string, IExerciseFilterOption>();
  exercises.forEach((exercise) => {
    getValues(exercise).forEach((value) => {
      const id = value?.id;
      if (!id || optionsById.has(id)) {
        return;
      }

      optionsById.set(id, {
        id,
        name: value.name || value.nameKey || '',
      });
    });
  });

  return Array.from(optionsById.values())
    .filter((option) => !!option.name)
    .sort((a, b) => a.name.localeCompare(b.name));
}

const ExerciseList = (props: {
  exercises: IExercise[] | null;
  filtersPageKey: string;
  showSearch?: boolean;
  addNewExerciseClicked?: () => void;
  afterAddToFavoriteAction?: () => void;
  isLoading?: boolean;
  listFooter?: React.ReactNode;
  disableClientFilter?: boolean;
  onQueryChange?: (query: IExerciseListFilterQuery) => void;
}) => {
  const {
    exercises,
    filtersPageKey,
    showSearch = true,
    addNewExerciseClicked,
    afterAddToFavoriteAction,
    isLoading = false,
    listFooter,
    disableClientFilter = false,
    onQueryChange,
  } = props;
  const [filter, setFilter] = useState('');
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const theme = useTheme();

  const { classes } = useStyles(theme);
  const { t } = useTranslation();
  const {
    exerciseToDeleteId,
    setExerciseToDeleteId,
    exerciseForPublish,
    setExerciseForPublish,
    onCopyClick,
    onEditClick,
    onDeleteClick,
    onFollowClick,
    onDeleteSubmit,
    onPublishSubmit,
    onPublishClick,
    onCancelPublishClick,
  } = useExerciseList(afterAddToFavoriteAction);

  const filterOptions = useMemo(
    () => ({
      categories: buildFilterOptions(exercises, (exercise) => [
        exercise.category,
      ]),
      muscles: buildFilterOptions(exercises, (exercise) => [
        exercise.mainMuscleGroup,
        ...(exercise.additionalMuscles ?? []),
      ]),
      equipment: buildFilterOptions(exercises, (exercise) => [
        exercise.equipment,
        ...(exercise.additionalEquipments ?? []),
      ]),
      developedSkills: buildFilterOptions(exercises, (exercise) => [
        ...(exercise.developedSkills ?? []),
      ]),
    }),
    [exercises],
  );

  const normalizeFilters = useCallback(
    (filters: IExerciseFilters) => {
      if (disableClientFilter) {
        return filters;
      }
      if (!exercises) {
        return filters;
      }

      return sanitizeExerciseFilters(filters, {
        categories: new Set(
          filterOptions.categories.map((option) => option.id),
        ),
        muscles: new Set(filterOptions.muscles.map((option) => option.id)),
        equipment: new Set(filterOptions.equipment.map((option) => option.id)),
        developedSkills: new Set(
          filterOptions.developedSkills.map((option) => option.id),
        ),
      });
    },
    [disableClientFilter, exercises, filterOptions],
  );

  const {
    draftFilters,
    appliedFilters,
    appliedFiltersCount,
    setDraftFilters,
    apply,
    cancel,
    reset,
  } = useExerciseFilters({
    pageKey: filtersPageKey,
    onClose: () => setIsFiltersOpen(false),
    normalizeFilters,
  });

  useEffect(() => {
    if (!disableClientFilter || !onQueryChange) {
      return;
    }
    onQueryChange({
      searchValue: filter,
      appliedFilters: cloneExerciseFilters(appliedFilters),
    });
  }, [disableClientFilter, filter, appliedFilters, onQueryChange]);

  const filteredExercises = useMemo(() => {
    if (disableClientFilter) {
      return exercises;
    }
    const filteredByCriteriaExercises = filterExercises(
      exercises,
      appliedFilters,
    );
    return filteredByCriteriaExercises.filter(
      (exercise) =>
        exercise.title.toLowerCase().indexOf(filter?.toLowerCase()) !== -1,
    );
  }, [disableClientFilter, exercises, appliedFilters, filter]);

  const showNoResult =
    !isLoading && !!filteredExercises && !filteredExercises?.length;

  return (
    <div className={classes.exerciseList}>
      <Box
        display="flex"
        alignItems="flex-end"
        justifyContent="flex-end"
        gap={1}>
        {showSearch && (
          <Search
            fullWidth
            value={filter}
            onChange={setFilter}
            className={classes.exerciseListSearch}
          />
        )}
        <FilterActionButton
          data-onboarding="exercsiePage.action.filters"
          selectedCount={appliedFiltersCount}
          onClick={() => setIsFiltersOpen(true)}
        />
        {addNewExerciseClicked && (
          <AppAddPillButton
            data-onboarding="exercsiePage.action.createExercise"
            onClick={() => addNewExerciseClicked()}
          />
        )}
      </Box>

      {showNoResult && <NoResults />}

      {isLoading && (
        <Box
          className={clsx(
            classes.exerciseListContainer,
            addNewExerciseClicked && classes.exerciseListContainerWithAddButton,
          )}
          display="flex"
          flexDirection="column">
          {Array.from({ length: SKELETON_COUNT }, (_, i) => (
            <ExerciseItemSkeleton key={i} />
          ))}
        </Box>
      )}

      {!isLoading && filteredExercises && filteredExercises.length > 0 && (
        <WithListObservers
          wrapperClassName={clsx(
            classes.exerciseListContainer,
            addNewExerciseClicked && classes.exerciseListContainerWithAddButton,
          )}>
          {filteredExercises.map((exercise) => (
            <React.Fragment key={exercise.id}>
              <ExerciseItem
                exercise={exercise}
                onEditClick={onEditClick}
                onDeleteClick={onDeleteClick}
                onCopyClick={onCopyClick}
                onPublishClick={onPublishClick}
                onCancelPublishClick={onCancelPublishClick}
                onFollowClick={onFollowClick}
              />
            </React.Fragment>
          ))}
          {listFooter}
        </WithListObservers>
      )}

      <ConfirmDialog
        open={!!exerciseToDeleteId}
        onClose={() => setExerciseToDeleteId(null)}
        title={t('common.delete')}
        onSubmit={onDeleteSubmit}>
        {t('common.deleteQuestion')}
      </ConfirmDialog>
      <AppDialog
        open={!!exerciseForPublish}
        onClose={() => setExerciseForPublish(null)}
        title={t('exercises.publishExercise')}>
        <PublishExerciseModal
          onCancel={() => setExerciseForPublish(null)}
          onSubmitCopyExercise={(canCopy) => {
            onPublishSubmit(canCopy);
          }}
        />
      </AppDialog>
      <ExerciseFiltersModal
        open={isFiltersOpen}
        filters={draftFilters}
        categories={filterOptions.categories}
        muscles={filterOptions.muscles}
        equipment={filterOptions.equipment}
        developedSkills={filterOptions.developedSkills}
        onChange={setDraftFilters}
        onCancel={cancel}
        onReset={reset}
        onApply={apply}
      />
    </div>
  );
};

export default ExerciseList;
